import { settingI18nKey, type CatalogSetting, type SettingValue } from '@nexus/core';
import { Input, SegmentedControl, Select, Switch } from '@nexus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface NavigationTarget {
  path: string;
  label: string;
}

/**
 * Control for one setting, chosen by its type from the manifest. The kernel renders every settings
 * page with it, modules declare only the definition (docs/architecture/modules.md).
 */
export function SettingControl({
  id,
  setting,
  value,
  onChange,
  targets = [],
  disabled,
}: {
  id: string;
  setting: CatalogSetting;
  value: SettingValue;
  onChange: (value: SettingValue) => void;
  /** Choices for `navigation-target` settings: pages the person can open. */
  targets?: readonly NavigationTarget[];
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const prefix = settingI18nKey(setting);
  const label = t(`${prefix}.label`);
  const [draft, setDraft] = useState<string | null>(null);
  // Arrangements are objects and have no control here.
  const text = typeof value === 'object' ? '' : String(value);

  switch (setting.type) {
    case 'boolean':
      return (
        <Switch
          id={id}
          aria-label={label}
          checked={value === true}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked)}
        />
      );
    case 'select': {
      const options = setting.options.map((option) => ({
        value: option,
        label: t(`${prefix}.options.${option}`),
      }));
      return options.length <= 3 ? (
        <SegmentedControl
          aria-label={label}
          value={text}
          onValueChange={onChange}
          options={options}
        />
      ) : (
        <Select
          id={id}
          aria-label={label}
          value={text}
          onValueChange={onChange}
          options={options}
          disabled={disabled}
          className="md:w-56"
        />
      );
    }
    case 'navigation-target':
      return (
        <Select
          id={id}
          aria-label={label}
          value={text}
          onValueChange={onChange}
          options={targets.map((target) => ({ value: target.path, label: target.label }))}
          disabled={disabled}
          className="md:w-56"
        />
      );
    case 'number':
    case 'text': {
      const numeric = setting.type === 'number';
      // Saved when leaving the field, not on every key stroke.
      return (
        <Input
          id={id}
          type={numeric ? 'number' : 'text'}
          inputMode={numeric ? (setting.integer ? 'numeric' : 'decimal') : undefined}
          min={numeric ? setting.min : undefined}
          max={numeric ? setting.max : undefined}
          maxLength={setting.type === 'text' ? setting.maxLength : undefined}
          value={draft ?? text}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            if (draft === null) return;
            const next = numeric ? Number(draft) : draft;
            setDraft(null);
            if (next !== value && !(numeric && Number.isNaN(next))) onChange(next);
          }}
          className="md:w-56"
        />
      );
    }
    case 'arrangement':
      // Edited on the page that shows the items; settings pages do not list it.
      return null;
  }
}
